import {useEffect, useRef, useState} from 'react'
import {Card} from 'react-bootstrap'
import {useMutation} from '@apollo/client'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {DELETE_AGENT_ONBOARDING_DOCUMENT, UPLOAD_AGENT_ONBOARDING_DOCUMENT_STR} from '../../../gql/mutations/agentOnboardingMutations'
import {useUploadFile} from '../../../hooks/useUploadFile'
import {URL as BACKEND} from '../../../gql/client'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {IAgentOnboardingDocument} from '../../../types/agentOnboarding'

const M = defineMessages({
  title: {id: 'onboarding.documents.title', defaultMessage: 'Documents'},
  subtitle: {
    id: 'onboarding.documents.subtitle',
    defaultMessage: 'Upload, replace or remove this applicant’s documents.',
  },
  downloadAll: {id: 'onboarding.documents.downloadAll', defaultMessage: 'Download all'},
  notUploaded: {id: 'onboarding.documents.notUploaded', defaultMessage: 'Not uploaded'},
  upload: {id: 'onboarding.documents.upload', defaultMessage: 'Upload'},
  replace: {id: 'onboarding.documents.replace', defaultMessage: 'Replace'},
  download: {id: 'onboarding.documents.download', defaultMessage: 'Download'},
  remove: {id: 'onboarding.documents.remove', defaultMessage: 'Remove'},
  confirmRemove: {
    id: 'onboarding.documents.confirmRemove',
    defaultMessage: 'Remove this document? This cannot be undone.',
  },
  uploaded: {id: 'onboarding.documents.uploaded', defaultMessage: 'Document uploaded'},
  uploadFailed: {id: 'onboarding.documents.uploadFailed', defaultMessage: 'Could not upload the document'},
  removed: {id: 'onboarding.documents.removed', defaultMessage: 'Document removed'},
  removeFailed: {id: 'onboarding.documents.removeFailed', defaultMessage: 'Could not remove the document'},
  downloadFailed: {id: 'onboarding.documents.downloadFailed', defaultMessage: 'Could not download the documents'},
  typeGovernmentId: {id: 'onboarding.documents.type.governmentId', defaultMessage: 'Government ID'},
  typeInsuranceLicense: {id: 'onboarding.documents.type.insuranceLicense', defaultMessage: 'Insurance license'},
  typeAmlCourseCertificate: {id: 'onboarding.documents.type.amlCourseCertificate', defaultMessage: 'AML course certificate'},
  typeWorkPermit: {id: 'onboarding.documents.type.workPermit', defaultMessage: 'Work permit'},
  typeEoCertificate: {id: 'onboarding.documents.type.eoCertificate', defaultMessage: 'E&O certificate'},
  typeW9: {id: 'onboarding.documents.type.w9', defaultMessage: 'W-9'},
  typeBankDocumentation: {id: 'onboarding.documents.type.bankDocumentation', defaultMessage: 'Bank documentation'},
  typeAgencyDocumentation: {id: 'onboarding.documents.type.agencyDocumentation', defaultMessage: 'Agency documentation'},
})

/** Mirrors AgentOnboardingDocument.DocumentType — every slot renders, uploaded or not. */
const DOCUMENT_TYPES = [
  'governmentId',
  'insuranceLicense',
  'amlCourseCertificate',
  'workPermit',
  'eoCertificate',
  'w9',
  'bankDocumentation',
  'agencyDocumentation',
] as const

interface Props {
  applicationId: string
  documents: IAgentOnboardingDocument[]
  canEdit: boolean
  onChanged: () => void
}

export function ApplicationDocuments({applicationId, documents, canEdit, onChanged}: Props) {
  const intl = useIntl()
  const inputs = useRef<Record<string, HTMLInputElement | null>>({})
  const [busyType, setBusyType] = useState<string | null>(null)
  const [uploadFile, {error: uploadError}] = useUploadFile<any>()
  const [deleteDocument] = useMutation(DELETE_AGENT_ONBOARDING_DOCUMENT)

  useEffect(() => {
    if (uploadError) toast.error(uploadError)
  }, [uploadError])

  const typeLabel = (type: string) =>
    intl.formatMessage(
      {
        governmentId: M.typeGovernmentId,
        insuranceLicense: M.typeInsuranceLicense,
        amlCourseCertificate: M.typeAmlCourseCertificate,
        workPermit: M.typeWorkPermit,
        eoCertificate: M.typeEoCertificate,
        w9: M.typeW9,
        bankDocumentation: M.typeBankDocumentation,
        agencyDocumentation: M.typeAgencyDocumentation,
      }[type] ?? M.typeGovernmentId
    )

  const byType = new Map(documents.map((d) => [d.documentType, d]))

  const handleFile = async (documentType: string, file: File) => {
    setBusyType(documentType)
    try {
      const formdata = new FormData()
      formdata.append('0', file, file.name)
      formdata.append('map', '{"0": ["variables.file"]}')
      formdata.append(
        'operations',
        JSON.stringify({
          query: UPLOAD_AGENT_ONBOARDING_DOCUMENT_STR,
          variables: {applicationId, documentType, file: null},
        })
      )
      const res = await uploadFile(formdata)
      const payload = res?.uploadAgentOnboardingDocument
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.uploadFailed))
        return
      }
      toast.success(intl.formatMessage(M.uploaded))
      onChanged()
    } catch {
      toast.error(intl.formatMessage(M.uploadFailed))
    } finally {
      setBusyType(null)
    }
  }

  const handleRemove = async (documentType: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(intl.formatMessage(M.confirmRemove))) return
    setBusyType(documentType)
    try {
      const res = await deleteDocument({variables: {applicationId, documentType}})
      const payload = res.data?.deleteAgentOnboardingDocument
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.removeFailed))
        return
      }
      toast.success(intl.formatMessage(M.removed))
      onChanged()
    } catch {
      toast.error(intl.formatMessage(M.removeFailed))
    } finally {
      setBusyType(null)
    }
  }

  // The zip cannot travel over GraphQL, so it comes from an HTTP view that expects the same
  // bearer token — hence fetch + blob instead of a plain link.
  const handleDownloadAll = async () => {
    try {
      const token = window.localStorage.getItem(STORAGE_NAMES.TOKEN)
      const res = await fetch(`${BACKEND}/onboarding/applications/${applicationId}/documents.zip`, {
        headers: {Authorization: `Bearer ${token}`},
      })
      if (!res.ok) throw new Error('download failed')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `onboarding-${applicationId}-documents.zip`
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(url)
      link.remove()
    } catch {
      toast.error(intl.formatMessage(M.downloadFailed))
    }
  }

  return (
    <Card className='mb-5'>
      <Card.Header className='border-0 pt-6 d-flex align-items-center justify-content-between'>
        <div>
          <h3 className='fw-bold text-gray-900 mb-1'>{intl.formatMessage(M.title)}</h3>
          <span className='text-gray-600 fs-7'>{intl.formatMessage(M.subtitle)}</span>
        </div>
        {documents.length > 0 && (
          <button type='button' className='btn btn-sm btn-light-primary' onClick={handleDownloadAll}>
            <i className='bi bi-download me-1' />
            {intl.formatMessage(M.downloadAll)}
          </button>
        )}
      </Card.Header>
      <Card.Body className='pt-2'>
        <div className='row g-4'>
          {DOCUMENT_TYPES.map((type) => {
            const doc = byType.get(type)
            const busy = busyType === type
            return (
              <div className='col-xl-6' key={type}>
                <div className='d-flex align-items-center justify-content-between gap-3 border border-gray-300 rounded p-4'>
                  <div className='d-flex align-items-center gap-3 min-w-0'>
                    <i
                      className={`bi fs-4 ${
                        doc ? 'bi-check-circle-fill text-success' : 'bi-exclamation-circle text-gray-400'
                      }`}
                    />
                    <div className='min-w-0'>
                      <div className='fw-semibold text-gray-900'>{typeLabel(type)}</div>
                      <div className='text-gray-500 fs-8 text-truncate'>
                        {doc?.fileName || intl.formatMessage(M.notUploaded)}
                      </div>
                    </div>
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    {doc?.fileUrl && (
                      <a
                        className='btn btn-icon btn-sm btn-light-primary'
                        href={doc.fileUrl}
                        target='_blank'
                        rel='noreferrer'
                        title={intl.formatMessage(M.download)}
                      >
                        <i className='bi bi-download' />
                      </a>
                    )}
                    {canEdit && (
                      <>
                        <input
                          type='file'
                          className='d-none'
                          accept='application/pdf,image/jpeg,image/png'
                          ref={(el) => {
                            inputs.current[type] = el
                          }}
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleFile(type, file)
                            e.target.value = ''
                          }}
                        />
                        <button
                          type='button'
                          className='btn btn-icon btn-sm btn-light'
                          disabled={busy}
                          title={intl.formatMessage(doc ? M.replace : M.upload)}
                          onClick={() => inputs.current[type]?.click()}
                        >
                          <i className='bi bi-upload' />
                        </button>
                        {doc && (
                          <button
                            type='button'
                            className='btn btn-icon btn-sm btn-light-danger'
                            disabled={busy}
                            title={intl.formatMessage(M.remove)}
                            onClick={() => handleRemove(type)}
                          >
                            <i className='bi bi-trash' />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </Card.Body>
    </Card>
  )
}
